import Template from "./template";
import paper from "paper";
import ComponentPort from "../core/componentPort";
import { LogicalLayerType } from "../core/init";
import { DEFAULT_CHANNEL_WIDTH_UM } from "./channelWidths";
import { resolveBinaryLeafCount } from "./branchLeafs";

export default class Mux extends Template {
    constructor() {
        super();
    }

    __setupDefinitions(): void  {
        this.__unique = {
            position: "Point"
        };

        this.__heritable = {
            in: "Integer",
            out: "Integer",
            flowChannelWidth: "Float",
            controlChannelWidth: "Float",
            leafSpace: "Float",
            stageSpace: "Float",
            valveWidthX: "Float",
            valveWidthY: "Float",
            depth: "Float",
            rotation: "Float",
            mirrorByX: "Float",
            mirrorByY: "Float",
            componentSpacing: "Float"
        };

        this.__defaults = {
            in: 1,
            out: 8,
            flowChannelWidth: DEFAULT_CHANNEL_WIDTH_UM,
            controlChannelWidth: DEFAULT_CHANNEL_WIDTH_UM,
            leafSpace: 4000,
            stageSpace: 4000,
            valveWidthX: 1800,
            valveWidthY: 1000,
            depth: 250,
            rotation: 0,
            mirrorByX: 0,
            mirrorByY: 0,
            componentSpacing: 2000
        };

        this.__units = {
            in: "",
            out: "",
            flowChannelWidth: "μm",
            controlChannelWidth: "μm",
            leafSpace: "μm",
            stageSpace: "μm",
            valveWidthX: "μm",
            valveWidthY: "μm",
            depth: "μm",
            rotation: "°",
            componentSpacing: "μm"
        };

        this.__minimum = {
            componentSpacing: 0,
            flowChannelWidth: 10,
            leafSpace: 100,
            in: 1,
            out: 2,
            valveWidthX: 60,
            valveWidthY: 60,
            depth: 10,
            stageSpace: 100,
            controlChannelWidth: 10,
            rotation: 0,
            mirrorByX: 0,
            mirrorByY: 0
        };

        this.__maximum = {
            componentSpacing: 10000,
            flowChannelWidth: 2000,
            leafSpace: 20000,
            in: 1,
            out: 1024,
            valveWidthX: 12 * 1000,
            valveWidthY: 12 * 1000,
            depth: 1200,
            stageSpace: 20000,
            controlChannelWidth: 2000,
            rotation: 360,
            mirrorByX: 1,
            mirrorByY: 1
        };

        this.__featureParams = {
            componentSpacing: "componentSpacing",
            position: "position",
            flowChannelWidth: "flowChannelWidth",
            controlChannelWidth: "controlChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            valveWidthX: "valveWidthX",
            valveWidthY: "valveWidthY",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__targetParams = {
            componentSpacing: "componentSpacing",
            position: "position",
            flowChannelWidth: "flowChannelWidth",
            controlChannelWidth: "controlChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            valveWidthX: "valveWidthX",
            valveWidthY: "valveWidthY",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__placementTool = "multilayerPositionTool";

        this.__toolParams = {
            position: "position"
        };

        this.__renderKeys = ["FLOW", "CONTROL"];

        this.__mint = "MUX";

        this.__zOffsetKeys = {
            FLOW: "depth",
            CONTROL: "depth"
        };

        this.__substrateOffset = {
            FLOW: "0",
            CONTROL: "+1"
        };
    }

    __drawFlow(params: { [k: string]: any }): paper.CompoundPath  {
        const position = params.position;
        const cw = params.flowChannelWidth;
        let rotation = params.rotation;
        // out must be ≥ 2 for MUX (binary tree + valves).
        const resolved = resolveBinaryLeafCount(params);
        const leafs = Math.max(2, resolved.leafs);
        // Orientation is params.rotation only. An extra 180° when in >= out
        // turns a stored rotation of 180 into 360°, so that MUX looks
        // unrotated, and the glyph no longer matches getPorts().
        const color = params.color;
        const stagelength = this.__muxStageSpace(params);
        const px = position[0];
        const py = position[1];

        const treepath = new paper.CompoundPath("");
        const levels = Math.log2(leafs);
        const w = this.__muxFanWidth(params, leafs, levels);
        this.__generateMuxTwig(treepath, px, py, cw, stagelength, w, 1, levels);

        treepath.fillColor = color;
        this.transformRender({ ...params, rotation }, treepath);
        return treepath;
    }

    __drawControl(params: { [k: string]: any }): paper.CompoundPath  {
        const position = params.position;
        const cw = params.flowChannelWidth;
        const ctlcw = params.controlChannelWidth;
        let rotation = params.rotation;
        const resolved = resolveBinaryLeafCount(params);
        const leafs = Math.max(2, resolved.leafs);
        const color = params.color;
        const stagelength = this.__muxStageSpace(params);
        const valvelength = this.__muxValveWidthY(params);
        const valvewidth = this.__muxValveWidthX(params);
        const px = position[0];
        const py = position[1];

        const treepath = new paper.CompoundPath("");
        const levels = Math.log2(leafs);
        const w = this.__muxFanWidth(params, leafs, levels);
        const pitch = 2 * 0.5 * w * 2 * Math.pow(0.5, levels);
        const treeWidth = this.__muxTreeWidth(pitch, leafs, cw, valvewidth);
        const leftEdge = px - treeWidth / 2;
        const rightEdge = px + treeWidth / 2;
        this.__generateMuxControlTwig(treepath, px, py, cw, ctlcw, stagelength, w, 1, levels, valvewidth, valvelength, leftEdge, rightEdge);

        treepath.fillColor = color;
        this.transformRender({ ...params, rotation }, treepath);
        return treepath;
    }

    getPorts(params: { [k: string]: any }) {
        const ports = [];
        const cw = params.flowChannelWidth;
        const leafs = Math.max(2, resolveBinaryLeafCount(params).leafs);
        const stagelength = this.__muxStageSpace(params);

        ports.push(new ComponentPort(0, 0, "1", LogicalLayerType.FLOW));

        const levels = Math.log2(leafs);
        const w = this.__muxFanWidth(params, leafs, levels);

        const length = levels * (cw + stagelength) + stagelength;
        const width = 2 * 0.5 * w * 2 * Math.pow(0.5, levels);

        for (let i = 0; i < leafs; i++) {
            ports.push(new ComponentPort(((leafs - 1) * width) / 2 - i * width, length, (2 + i).toString(), LogicalLayerType.FLOW));
        }

        let count = 2 + leafs;
        const lstarty = stagelength + cw;
        const valveAlong = this.__muxValveAlong(stagelength, cw, this.__muxValveWidthY(params));
        const offsets = this.__muxValveCenterOffsets(stagelength, valveAlong);
        const lcentery = lstarty + offsets.left;
        const valvewidth = this.__muxValveWidthX(params);
        const treeWidth = this.__muxTreeWidth(width, leafs, cw, valvewidth);

        const leftEdge = -treeWidth / 2;
        const rightEdge = treeWidth / 2;

        const rstarty = stagelength + cw;
        const rcentery = rstarty + offsets.right;

        for (let i = 0; i < levels; i++) {
            ports.push(new ComponentPort(leftEdge, i * (cw + stagelength) + lcentery, count.toString(), LogicalLayerType.CONTROL));
            count++;
            ports.push(new ComponentPort(rightEdge, i * (cw + stagelength) + rcentery, count.toString(), LogicalLayerType.CONTROL));
            count++;
        }

        return ports;
    }

    render2D(params: { [k: string]: any }, key: string): paper.CompoundPath  {
        if (key === "FLOW") {
            return this.__drawFlow(params);
        } else if (key === "CONTROL") {
            return this.__drawControl(params);
        }
        throw new Error("Unknown key: " + key);
    }

    render2DTarget(key: string | null, params: { [k: string]: any }): paper.CompoundPath  {
        const render = this.render2D(params, "FLOW");
        render?.addChild(this.render2D(params, "CONTROL") as paper.Item);
        render!.fillColor!.alpha = 0.5;
        return render;
    }

    __generateMuxTwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, newspacing: number, level: number, maxlevel: number, islast = false): void  {
        // var newspacing = 2 * (spacing + cw);
        const hspacing = newspacing / 2;
        const lex = px - 0.5 * newspacing;
        const ley = py + cw + stagelength;
        const rex = px + 0.5 * newspacing;
        const rey = py + cw + stagelength;

        if (level === maxlevel) {
            islast = true;
            // console.log("Final Spacing: " + newspacing)
        }

        this.__drawmuxtwig(treepath, px, py, cw, stagelength, newspacing, islast);

        if (!islast) {
            this.__generateMuxTwig(treepath, lex, ley, cw, stagelength, hspacing, level + 1, maxlevel);
            this.__generateMuxTwig(treepath, rex, rey, cw, stagelength, hspacing, level + 1, maxlevel);
        }
    }

    __muxValveWidthX(params: { [k: string]: any }): number {
        const named = Number(params.valveWidthX);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const mid = Number(params.valveWidth);
        if (Number.isFinite(mid) && mid > 0) {
            return mid;
        }
        const legacy = Number(params.width);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 1800;
    }

    __muxValveWidthY(params: { [k: string]: any }): number {
        const named = Number(params.valveWidthY);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.length);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 1000;
    }

    __muxLeafSpace(params: { [k: string]: any }): number {
        const named = Number(params.leafSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.leafPitch);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 0;
    }

    __muxStageSpace(params: { [k: string]: any }): number {
        const named = Number(params.stageSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.stageLength);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 4000;
    }

    __muxFanWidth(params: { [k: string]: any }, leafs: number, levels: number): number {
        const leafSpace = this.__muxLeafSpace(params);
        if (leafSpace > 0) {
            return leafSpace * Math.pow(2, Math.max(levels - 1, 0));
        }
        const spacing = Number(params.spacing || 0);
        if (spacing > 0) {
            return spacing * (leafs / 2 + 1);
        }
        return 4000 * Math.pow(2, Math.max(levels - 1, 0));
    }

    __muxTreeWidth(pitch: number, leafs: number, cw: number, valvewidth: number): number {
        // Envelope tracks the leaf fan, plus one horizontal valve past the outer channel.
        return (leafs - 1) * pitch + valvewidth + 2 * cw;
    }

    __muxValveAlong(stagelength: number, cw: number, valvelength: number): number {
        // Honor valveWidthY; only keep the pad inside the vertical stage.
        const fit = Math.max(stagelength - 2 * Math.max(cw, 0), Math.max(cw, 1));
        const wanted = valvelength > 0 ? valvelength : 1000;
        return Math.min(wanted, fit);
    }

    __muxValveAcross(valvewidth: number, _twigSpacing: number, cw: number): number {
        // Honor valveWidthX so the pad can grow left/right without a pitch cap.
        return valvewidth > 0 ? valvewidth : Math.max(cw * 2.8, 1);
    }

    __muxValveCenterOffsets(stagelength: number, along: number): { left: number; right: number } {
        // Spread left/right control buses across the stage without growing
        // stageSpace. Same-stage center gap is 0.4*slack (0.3 / 0.7 of slack).
        const slack = Math.max(stagelength - along, 0);
        const half = along / 2;
        return {
            left: half + slack * 0.3,
            right: half + slack * 0.7
        };
    }

    __drawmuxtwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, spacing: number, drawleafs = false): paper.CompoundPath  {
        // stem
        let startPoint = new paper.Point(px - cw / 2, py);
        let endPoint = new paper.Point(px + cw / 2, py + stagelength);
        let rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });

        treepath.addChild(rec);

        // Draw 2 leafs
        // left leaf
        const lstartx = px - 0.5 * (cw + spacing);
        const lendx = lstartx + cw;
        const lstarty = py + stagelength + cw;
        const lendy = lstarty + stagelength;

        // //right leaf
        const rstartx = px + 0.5 * (spacing - cw);
        const rendx = rstartx + cw;
        const rstarty = py + stagelength + cw;
        const rendy = rstarty + stagelength;

        if (drawleafs) {
            startPoint = new paper.Point(lstartx, lstarty);
            endPoint = new paper.Point(lendx, lendy);
            rec = new paper.Path.Rectangle({
                from: startPoint,
                to: endPoint,
                radius: 0,
                strokeWidth: 0
            });
            treepath.addChild(rec);

            startPoint = new paper.Point(rstartx, rstarty);
            endPoint = new paper.Point(rendx, rendy);
            rec = new paper.Path.Rectangle({
                from: startPoint,
                to: endPoint,
                radius: 0,
                strokeWidth: 0
            });
            treepath.addChild(rec);
        }

        // Horizontal bar
        const hstartx = px - 0.5 * (cw + spacing);
        const hendx = rendx;
        const hstarty = py + stagelength;
        const hendy = hstarty + cw;
        startPoint = new paper.Point(hstartx, hstarty);
        endPoint = new paper.Point(hendx, hendy);
        rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });
        treepath.addChild(rec);
        return treepath;
    }

    __generateMuxControlTwig(
        treepath: paper.CompoundPath,
        px: number,
        py: number,
        cw: number,
        ctlcw: number,
        stagelength: number,
        newspacing: number,
        level: number,
        maxlevel: number,
        valvewidth: number,
        valvelength: number,
        leftEdge: number,
        rightEdge: number,
        islast = false
    ): void  {
        // var newspacing = 2 * (spacing + cw);
        const hspacing = newspacing / 2;
        const lex = px - 0.5 * newspacing;
        const ley = py + cw + stagelength;
        const rex = px + 0.5 * newspacing;
        const rey = py + cw + stagelength;

        if (level === maxlevel) {
            islast = true;
            // console.log("Final Spacing: " + newspacing)
        }

        this.__drawmuxcontroltwig(treepath, px, py, cw, ctlcw, stagelength, newspacing, valvewidth, valvelength, leftEdge, rightEdge, islast);

        if (!islast) {
            this.__generateMuxControlTwig(treepath, lex, ley, cw, ctlcw, stagelength, hspacing, level + 1, maxlevel, valvewidth, valvelength, leftEdge, rightEdge);
            this.__generateMuxControlTwig(treepath, rex, rey, cw, ctlcw, stagelength, hspacing, level + 1, maxlevel, valvewidth, valvelength, leftEdge, rightEdge);
        }
    }

    __drawmuxcontroltwig(
        treepath: paper.CompoundPath,
        px: number,
        py: number,
        cw: number,
        ctlcw: number,
        stagelength: number,
        spacing: number,
        valvewidth: number,
        valvelength: number,
        leftEdge: number,
        rightEdge: number,
        drawleafs = false
    ): paper.CompoundPath  {
        // stem - don't bother with valves

        // Draw 2 valves
        // left leaf
        const lstartx = px - 0.5 * (cw + spacing);
        const lendx = lstartx + cw;
        const lstarty = py + stagelength + cw;
        const lendy = lstarty + stagelength;

        const lcenterx = (lstartx + lendx) / 2;
        const thick = this.__muxValveAlong(stagelength, cw, valvelength);
        const across = this.__muxValveAcross(valvewidth, spacing, cw);
        const offsets = this.__muxValveCenterOffsets(stagelength, thick);
        const lcentery = lstarty + offsets.left;

        // //right leaf
        const rstartx = px + 0.5 * (spacing - cw);
        const rendx = rstartx + cw;
        const rstarty = py + stagelength + cw;
        const rendy = rstarty + stagelength;

        const rcenterx = (rstartx + rendx) / 2;
        const rcentery = rstarty + offsets.right;

        let startPoint = new paper.Point(lcenterx - across / 2, lcentery - thick / 2);
        let endPoint = new paper.Point(lcenterx + across / 2, lcentery + thick / 2);
        let rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });
        treepath.addChild(rec);

        const leftChannelStart = new paper.Point(startPoint.x, lcentery - ctlcw / 2);
        const leftChannelEnd = new paper.Point(leftEdge, lcentery + ctlcw / 2);

        const leftChannel = new paper.Path.Rectangle({
            from: leftChannelStart,
            to: leftChannelEnd,
            radius: 0,
            strokeWidth: 0
        });
        treepath.addChild(leftChannel);

        startPoint = new paper.Point(rcenterx - across / 2, rcentery - thick / 2);
        endPoint = new paper.Point(rcenterx + across / 2, rcentery + thick / 2);
        rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });

        treepath.addChild(rec);
        const rightChannelStart = new paper.Point(endPoint.x, rcentery - ctlcw / 2);
        const rightChannelEnd = new paper.Point(rightEdge, rcentery + ctlcw / 2);

        const rightChannel = new paper.Path.Rectangle({
            from: rightChannelStart,
            to: rightChannelEnd,
            radius: 0,
            strokeWidth: 0
        });
        treepath.addChild(rightChannel);

        return treepath;
    }
}
